import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { STRINGS } from "@/i18n";
import client from "@/lib/apiClient";

const MAX_POLLS = 20;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { lang } = useLang();
  const { clear } = useCart();
  const t = STRINGS[lang].payment;
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [orderNumber, setOrderNumber] = useState("");
  const cleared = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await client.get(`/payments/status/${sessionId}`);
        if (data.order_number) setOrderNumber(data.order_number);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (attempts >= MAX_POLLS) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, clear]);

  const Icon = state === "paid" ? CheckCircle2 : state === "error" ? XCircle : Loader2;
  const title = state === "paid" ? t.successTitle : state === "error" ? t.errorTitle : state === "pending" ? t.pendingTitle : t.checkingTitle;
  const text = state === "paid" ? t.successText : state === "error" ? t.errorText : state === "pending" ? t.pendingText : t.checkingText;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="payment-success-page">
      <div className="max-w-md text-center">
        <Icon className={`h-16 w-16 mx-auto mb-6 ${state === "paid" ? "text-success" : state === "error" ? "text-destructive" : "text-muted-foreground animate-spin"}`} strokeWidth={1.3} />
        <h1 className="font-display text-3xl mb-3" data-testid="payment-status-title">{title}</h1>
        {state === "paid" && orderNumber && (
          <p className="font-mono text-sm text-muted-foreground mb-2" data-testid="order-number">
            {t.orderLabel}: <span className="text-foreground">{orderNumber}</span>
          </p>
        )}
        <p className="text-muted-foreground font-light mb-8">{text}</p>
        <Link to="/" className="inline-block h-12 px-8 leading-[3rem] bg-primary text-primary-foreground hover:bg-white/90 uppercase tracking-wide rounded-md text-sm font-semibold" data-testid="back-home-button">
          {t.backHome}
        </Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { lang } = useLang();
  const t = STRINGS[lang].payment;
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="payment-cancel-page">
      <div className="max-w-md text-center">
        <XCircle className="h-16 w-16 mx-auto mb-6 text-muted-foreground" strokeWidth={1.3} />
        <h1 className="font-display text-3xl mb-3">{t.cancelTitle}</h1>
        <p className="text-muted-foreground font-light mb-8">{t.cancelText}</p>
        <Link to="/" className="inline-block h-12 px-8 leading-[3rem] bg-primary text-primary-foreground hover:bg-white/90 uppercase tracking-wide rounded-md text-sm font-semibold">
          {t.backHome}
        </Link>
      </div>
    </div>
  );
}
